import { bytesToHex } from '../encoding';

export interface ByteInterpretation {
  offset: number;
  hex: string;
  binary: string;
  unsigned8: number;
  signed8: number;
  ascii: string;
  utf8: string;
  uint16Le?: number;
  uint16Be?: number;
  int16Le?: number;
  int16Be?: number;
  uint32Le?: number;
  uint32Be?: number;
  int32Le?: number;
  int32Be?: number;
  float32Le?: number;
  float32Be?: number;
  float64Le?: number;
  float64Be?: number;
}

export function interpretByteAt(bytes: Uint8Array, offset: number): ByteInterpretation {
  if (!Number.isInteger(offset) || offset < 0 || offset >= bytes.length) {
    throw new Error('Byte offset is outside the input');
  }

  const available = bytes.length - offset;
  const view = new DataView(bytes.buffer, bytes.byteOffset + offset, available);
  const value = bytes[offset];
  const utf8Length =
    value <= 0x7f
      ? 1
      : value >= 0xc2 && value <= 0xdf
        ? 2
        : value <= 0xef
          ? 3
          : value <= 0xf4
            ? 4
            : 0;
  let utf8 = '';
  try {
    if (utf8Length === 0 || available < utf8Length) throw new Error('Invalid UTF-8 lead byte');
    utf8 = new TextDecoder('utf-8', { fatal: true }).decode(
      bytes.subarray(offset, offset + utf8Length),
    );
  } catch {
    utf8 = 'Invalid at this offset';
  }

  const result: ByteInterpretation = {
    offset,
    hex: bytesToHex(bytes.subarray(offset, Math.min(bytes.length, offset + 8))),
    binary: value.toString(2).padStart(8, '0'),
    unsigned8: value,
    signed8: view.getInt8(0),
    ascii: value >= 32 && value <= 126 ? String.fromCharCode(value) : '.',
    utf8,
  };

  if (available >= 2) {
    result.uint16Le = view.getUint16(0, true);
    result.uint16Be = view.getUint16(0, false);
    result.int16Le = view.getInt16(0, true);
    result.int16Be = view.getInt16(0, false);
  }
  if (available >= 4) {
    result.uint32Le = view.getUint32(0, true);
    result.uint32Be = view.getUint32(0, false);
    result.int32Le = view.getInt32(0, true);
    result.int32Be = view.getInt32(0, false);
    result.float32Le = view.getFloat32(0, true);
    result.float32Be = view.getFloat32(0, false);
  }
  if (available >= 8) {
    result.float64Le = view.getFloat64(0, true);
    result.float64Be = view.getFloat64(0, false);
  }

  return result;
}
